/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Onboarding API endpoints.
 *
 * GET  /api/v1/auth/onboarding/ - fetch (or auto-create) my progress
 * POST /api/v1/auth/onboarding/step/ - mark a step complete or set current
 * POST /api/v1/auth/onboarding/dismiss/ - never show overlay again
 * POST /api/v1/auth/onboarding/reset/ - clear state, start over
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { OnboardingProgress } from "../model/types";

/** RTK Query endpoints for onboarding progress. */
export const onboardingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOnboardingProgress: build.query<OnboardingProgress, void>({
      query: () => "/auth/onboarding/",
      providesTags: [{ type: "OnboardingProgress", id: "ME" }],
    }),
    markOnboardingStep: build.mutation<
      OnboardingProgress,
      { step_key: string; is_current?: boolean }
    >({
      query: (body) => ({
        url: "/auth/onboarding/step/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OnboardingProgress", id: "ME" }],
    }),
    /** Soft-skip ONE step ("Skip for now"). Distinct from `dismiss`,
     *  which is the hard "never show again" opt-out. */
    skipOnboardingStep: build.mutation<
      OnboardingProgress,
      { step_key: string }
    >({
      query: (body) => ({
        url: "/auth/onboarding/skip-step/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OnboardingProgress", id: "ME" }],
    }),
    dismissOnboarding: build.mutation<OnboardingProgress, void>({
      query: () => ({
        url: "/auth/onboarding/dismiss/",
        method: "POST",
      }),
      invalidatesTags: [{ type: "OnboardingProgress", id: "ME" }],
    }),
    resetOnboarding: build.mutation<OnboardingProgress, void>({
      query: () => ({
        url: "/auth/onboarding/reset/",
        method: "POST",
      }),
      invalidatesTags: [{ type: "OnboardingProgress", id: "ME" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOnboardingProgressQuery,
  useMarkOnboardingStepMutation,
  useSkipOnboardingStepMutation,
  useDismissOnboardingMutation,
  useResetOnboardingMutation,
} = onboardingApi;
